import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { cn, formatNumber } from "@/lib/utils";

/*
 * Chart conventions here follow the dataviz method:
 *  - Series colours come from the validated palette in index.css, assigned in
 *    FIXED slot order. Never cycle, never generate a hue for an extra series.
 *  - Three light-mode slots sit under 3:1 against the card, so every chart
 *    ships a legend AND direct labels (the relief rule). Colour never carries
 *    meaning alone.
 *  - One axis. Never two y-scales.
 *  - Recessive grid/axes, thin marks, rounded data-ends, a 2px surface gap
 *    between adjacent fills.
 */

export const SERIES = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--series-5)",
  "var(--series-6)",
] as const;

export const STATUS_COLOR = {
  good: "var(--status-good)",
  warning: "var(--status-warning)",
  serious: "var(--status-serious)",
  critical: "var(--status-critical)",
} as const;

export interface Datum {
  label: string;
  value: number;
  color?: string;
}

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: Datum }[];
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-popover px-2.5 py-1.5 shadow-md">
      <p className="text-xs font-medium">{d.label}</p>
      <p className="tabular text-xs text-muted-foreground">
        {formatNumber(d.value)}
      </p>
    </div>
  );
}

/** Horizontal bars — the default for ranked magnitude with readable labels. */
export function RankedBars({
  data,
  height = 220,
  emptyLabel = "No data yet",
}: {
  data: Datum[];
  height?: number;
  emptyLabel?: string;
}) {
  if (!data.length || data.every((d) => d.value === 0)) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 36, bottom: 4, left: 4 }}
        barCategoryGap={6}
      >
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="label"
          width={128}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: "var(--chart-ink-muted)" }}
        />
        <Tooltip
          cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
          content={<ChartTooltip />}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
          {data.map((d, i) => (
            <Cell
              key={d.label}
              fill={d.color ?? SERIES[i % SERIES.length]}
              stroke="var(--chart-surface)"
              strokeWidth={2}
            />
          ))}
          {/* Direct labels are required here, not optional: three light-mode
              slots sit under 3:1 on the card surface. Text wears the ink
              token, never the series colour. */}
          <LabelList
            dataKey="value"
            position="right"
            offset={8}
            formatter={(v: number) => formatNumber(v)}
            style={{
              fill: "var(--chart-ink)",
              fontSize: 11,
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Donut with a legend and per-slice counts — never colour alone. */
export function StatusDonut({
  data,
  height = 200,
}: {
  data: Datum[];
  height?: number;
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height }}
      >
        Nothing to show yet
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div style={{ width: 168, height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius="58%"
              outerRadius="86%"
              paddingAngle={2}
              stroke="var(--chart-surface)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {data.map((d, i) => (
                <Cell key={d.label} fill={d.color ?? SERIES[i % SERIES.length]} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* The legend doubles as the direct-label channel. */}
      <ul className="min-w-[9rem] flex-1 space-y-1.5">
        {data.map((d, i) => (
          <li key={d.label} className="flex items-center gap-2 text-xs">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-sm"
              style={{ background: d.color ?? SERIES[i % SERIES.length] }}
            />
            <span className="flex-1 truncate text-muted-foreground">{d.label}</span>
            <span className="tabular font-medium">{formatNumber(d.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Big single number — a stat tile, not a chart. No hover layer needed. */
export function StatTile({
  label,
  value,
  hint,
  tone = "default",
  icon: Icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "default" | "good" | "warning" | "critical";
  icon?: React.ElementType;
}) {
  const accent =
    tone === "good"
      ? STATUS_COLOR.good
      : tone === "warning"
        ? STATUS_COLOR.warning
        : tone === "critical"
          ? STATUS_COLOR.critical
          : "var(--series-1)";

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-card p-4 shadow-sm">
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: accent }}
      />
      <div className="flex items-start justify-between gap-2 pl-2">
        <div className="min-w-0">
          <p className="truncate text-xs text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight">
            {typeof value === "number" ? formatNumber(value) : value}
          </p>
          {hint && (
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</p>
          )}
        </div>
        {Icon && (
          <span
            className="rounded-md p-1.5"
            style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}
          >
            <Icon className="size-4" style={{ color: accent }} />
          </span>
        )}
      </div>
    </div>
  );
}

export interface TrendPoint {
  date: string;
  placed: number;
  completed: number;
  cancelled: number;
}

/*
 * The three trend series use categorical slots 1–3, which validate all-pairs in
 * both modes but land in the 6–8 CVD warn band (worst ΔE 7.3 light / 7.5 dark).
 * That band is legal ONLY with secondary encoding, so each line also carries a
 * distinct dash pattern and the legend repeats it. Status green/red were the
 * obvious choice for completed/cancelled and were rejected: they measure ΔE 4.1
 * under deuteranopia — the classic red/green collision.
 */
const TREND_SERIES = [
  { key: "placed", label: "Placed", color: "var(--series-2)", dash: undefined },
  { key: "completed", label: "Completed", color: "var(--series-1)", dash: "6 3" },
  { key: "cancelled", label: "Cancelled", color: "var(--series-3)", dash: "2 3" },
] as const;

function TrendTooltip({
  active,
  label,
  payload,
}: {
  active?: boolean;
  label?: string;
  payload?: { dataKey: string; value: number; color: string }[];
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 shadow-md">
      <p className="mb-1 text-xs font-medium">
        {label ? new Date(label).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        }) : ""}
      </p>
      <ul className="space-y-0.5">
        {payload.map((p) => {
          const series = TREND_SERIES.find((s) => s.key === p.dataKey);
          return (
            <li key={p.dataKey} className="flex items-center gap-2 text-xs">
              <span
                aria-hidden
                className="h-0.5 w-3 shrink-0 rounded-full"
                style={{ background: p.color }}
              />
              <span className="text-muted-foreground">{series?.label}</span>
              <span className="tabular ml-auto font-medium">{p.value}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Orders over time. One y-axis — never two scales on one chart. */
export function TrendChart({
  data,
  height = 260,
}: {
  data: TrendPoint[];
  height?: number;
}) {
  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height }}
      >
        No activity in this range
      </div>
    );
  }

  return (
    <div>
      {/* Legend is always present for ≥2 series, and repeats the dash so
          identity never rests on hue alone. */}
      <ul className="mb-2 flex flex-wrap gap-4">
        {TREND_SERIES.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5 text-xs">
            <svg width="18" height="6" aria-hidden>
              <line
                x1="0"
                y1="3"
                x2="18"
                y2="3"
                stroke={s.color}
                strokeWidth="2"
                strokeDasharray={s.dash}
                strokeLinecap="round"
              />
            </svg>
            <span className="text-muted-foreground">{s.label}</span>
          </li>
        ))}
      </ul>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 6, right: 12, bottom: 4, left: -18 }}>
          <CartesianGrid
            stroke="var(--chart-grid)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={{ stroke: "var(--chart-axis)" }}
            tick={{ fontSize: 11, fill: "var(--chart-ink-muted)" }}
            tickFormatter={(v: string) =>
              new Date(v).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
              })
            }
            minTickGap={24}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "var(--chart-ink-muted)" }}
            width={44}
          />
          <Tooltip
            content={<TrendTooltip />}
            cursor={{ stroke: "var(--chart-axis)", strokeWidth: 1 }}
          />
          {TREND_SERIES.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              strokeDasharray={s.dash}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--chart-surface)" }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Slim proportion bar with a 2px surface gap between segments. */
export function ProportionBar({
  segments,
  className,
}: {
  segments: Datum[];
  className?: string;
}) {
  const total = segments.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  return (
    <div className={cn("flex h-2 w-full gap-0.5 overflow-hidden", className)}>
      {segments.map((s, i) => (
        <span
          key={s.label}
          title={`${s.label}: ${formatNumber(s.value)}`}
          className="h-full rounded-sm first:rounded-l-full last:rounded-r-full"
          style={{
            width: `${(s.value / total) * 100}%`,
            background: s.color ?? SERIES[i % SERIES.length],
          }}
        />
      ))}
    </div>
  );
}
